import { useCallback, useEffect, useRef, useState } from "react";
import { getLiveFeed } from "../../api/monitoringAPI";

const MAX_ROWS = 300;

const msClass = (v) => (v == null ? "text-text-muted" : v >= 1500 ? "text-rose-500" : v >= 600 ? "text-amber-500" : "text-emerald-500");
const statusClass = (s) => (s == null ? "" : s >= 500 ? "text-rose-500" : s >= 400 ? "text-amber-500" : "text-emerald-500");

function describe(e) {
  if (e.api_endpoint) return `${e.http_method || ""} ${e.api_endpoint}`;
  if (e.page_name) return `экран: ${e.page_name}`;
  if (e.search_query) return `іздеу: «${e.search_query}»`;
  return e.event_name || e.event_type;
}

/** Тірі ағын: кім, қай платформадан, не істеді / қандай сұрау, қанша мс. */
export default function LiveFeed() {
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("");
  const [userId, setUserId] = useState("");
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState(null);
  const lastId = useRef(null);

  const poll = useCallback(async (reset = false) => {
    try {
      const res = await getLiveFeed({
        limit: reset ? 100 : 200,
        source: source || undefined,
        user_id: userId || undefined,
        after_id: reset ? undefined : lastId.current || undefined,
      });
      if (res.items.length) lastId.current = res.items[0].id;
      setRows((prev) => (reset ? res.items : [...res.items, ...prev]).slice(0, MAX_ROWS));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [source, userId]);

  useEffect(() => {
    lastId.current = null;
    poll(true);
  }, [poll]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => poll(false), 5000);
    return () => clearInterval(t);
  }, [paused, poll]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="rounded-lg border border-border-primary bg-bg-card px-3 py-2 text-sm text-text-primary"
        >
          <option value="">Барлық көз</option>
          <option value="server">Сервер (прод)</option>
          <option value="srv-test">Сервер (тест)</option>
          <option value="app">Қосымшалар</option>
        </select>
        <input
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))}
          placeholder="Қолданушы ID"
          className="w-40 rounded-lg border border-border-primary bg-bg-card px-3 py-2 text-sm text-text-primary placeholder:text-text-muted"
        />
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          className={`rounded-lg px-3 py-2 text-sm font-medium ${paused ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/15 text-emerald-500"}`}
        >
          {paused ? "⏸ Тоқтатулы" : "● Тірі (5 с)"}
        </button>
        <span className="text-xs text-text-muted">{rows.length} жол</span>
      </div>

      {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-500">{error}</div>}

      <div className="max-h-[70vh] overflow-auto rounded-xl border border-border-primary bg-bg-card">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-bg-card text-left text-xs text-text-muted">
            <tr>
              <th className="px-4 py-2">Уақыт</th>
              <th className="px-2 py-2">Қолданушы</th>
              <th className="px-2 py-2">Платформа</th>
              <th className="px-2 py-2">Оқиға / сұрау</th>
              <th className="px-2 py-2 text-right">Статус</th>
              <th className="px-4 py-2 text-right">Уақыты</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {rows.map((e) => (
              <tr key={e.id} className="hover:bg-bg-tertiary/60">
                <td className="whitespace-nowrap px-4 py-1.5 text-xs text-text-muted">
                  {e.timestamp ? new Date(e.timestamp).toLocaleTimeString("ru-RU") : "—"}
                </td>
                <td className="px-2 py-1.5">
                  {e.user_id ? (
                    <button type="button" onClick={() => setUserId(String(e.user_id))} className="text-[#7C3AED] hover:underline">
                      #{e.user_id}
                    </button>
                  ) : (
                    <span className="text-text-muted">гость</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-xs text-text-secondary">
                  {e.platform || "—"}{e.app_version ? ` ${e.app_version}` : ""}
                </td>
                <td className="max-w-[420px] truncate px-2 py-1.5 font-mono text-xs text-text-primary" title={describe(e)}>
                  {describe(e)}
                  {e.error_message && <span className="ml-2 text-rose-500">{e.error_message}</span>}
                </td>
                <td className={`px-2 py-1.5 text-right text-xs ${statusClass(e.status_code)}`}>{e.status_code ?? ""}</td>
                <td className={`px-4 py-1.5 text-right text-xs font-semibold ${msClass(e.duration_ms)}`}>
                  {e.duration_ms != null ? `${Math.round(e.duration_ms)} мс` : ""}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-text-muted">Оқиға әлі жоқ</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
